import React, { useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Row, Col, Container, Button, Image, Alert, Form } from 'react-bootstrap';
import { useStoreState, useStoreActions } from 'easy-peasy';

import HelperMetiers360 from '../../services/HelpersMetiers360.js';

import AuthorizationChecker from "../../services/AuthorizationChecker";
import { useModal } from '../../hooks/useModal';
import { useToast } from "../../hooks/useToast.jsx";

import ActivityStatusModal from './ActivityStatusModal.jsx';
import './ActivityDetailsComponent.scss';
import useBackButton from '../../hooks/useBackButton.jsx';
import Webapp2ActivitiesPDFTable from "../clients/ClientsDetail/ClientDetailTabs/ClientWebapp2TabComponent/Webapp2ActivitiesPDFTable.jsx";

/**
 * 
 * @param {Object} activity object with all the activity data to be displayed on the page
 * @example
 * 
 * <ActivityDetailsComponent activity={activity}>
 */

const ActivityDetailsComponent = (props) => {
    const { activity, returnLink } = props;

    const activitySlugs = useStoreState(actions => actions.actionSlugs.actionSlugsDispatcher('activity'));
    const { deleteActivity, editActivityStatus } = useStoreActions(actions => actions.activity);
    const { editClientActivityBlacklist } = useStoreActions(actions => actions.clients);
    const { isParent, client: clientUniqueId } = useStoreState(state => state.auth);
    const { getIsParent } = useStoreActions(state => state.auth);

    const { apiData: { BACKOFFICE_DEFAULT_VALUES } } = useStoreState(state => state.actionSlugs);
    const { activityStatus, activityAudienceTypes } = BACKOFFICE_DEFAULT_VALUES;

    const { modalComponent, setModalShow, modalData, setModalData } = useModal();

    const clientsSlugs = useStoreState(actions => actions.actionSlugs.actionSlugsDispatcher('clients'));

    const navigate = useNavigate();
    const toast = useToast();

    const goToClient = (uniqueIdClient) => {
        navigate(clientsSlugs.readOne.replace(':uniqueId', uniqueIdClient))
    }
    const { backButtonComponent } = useBackButton();
    const { isMobile, isTablet } = useStoreState(state => state.responsive);

    useEffect(() => {
        if (isParent === null && !AuthorizationChecker.isAdmin()) {
            getIsParent();
        }
    }, []);

    const clientsList = activity?.clientsListData?.length > 0
        ? activity.clientsListData?.map((p, i) =>
            <span key={`link${p.uniqueId}`}>
                <a href="#"
                    onClick={(e) => { e.preventDefault(); goToClient(p.uniqueId); }}>
                    {p.clientName}
                </a>
                {(i === activity?.clientsListData?.length - 1) ? null : <span>,&nbsp;&nbsp;</span>}
            </span>)
        : <i>Aucune</i>;

    const displayAudienceTypes = activity?.audienceType?.length > 0 ?
        activity?.audienceType?.map(audienceT => activityAudienceTypes?.find(at => at.value == audienceT)?.label)?.join(", ")
        : <i>Tout le monde</i>;

    const createDeleteModal = () => {
        setModalData({
            ...modalData,
            header: <>Suppression de l'activité</>,
            content: <h5>Êtes-vous sûr⋅e de vouloir supprimer l'activité <b>{activity.title}</b>&nbsp;?</h5>,
            resetButton: 'Supprimer',
            onReset:
                () => {
                    deleteActivity({ uniqueId: activity.uniqueId })
                        .then(() => {
                            toast.open({ message: "Activité supprimée", variant: "success" });
                            navigate(returnLink)
                        });
                }
        });
        setModalShow(true);
    }

    const createStatusModal = () => {

        setModalData({
            ...modalData,
            header: <>Modifier le statut de l'activité</>,
            content: <ActivityStatusModal />,
            cancelButton: 'Annuler',
            onValidate:
                () => {
                    const statusValue = document.getElementById('statusSelected')?.value;
                    (statusValue && statusValue !== "") && editActivityStatus({ uniqueId: activity.uniqueId, status: statusValue })
                        .then(() => {
                            toast.open({ message: "Statut de l'activité modifié", variant: "success" });
                            setModalShow(false)
                        });
                }
        });
        setModalShow(true);
    }

    const createBlacklistModal = (activity) => {
        setModalData({
            ...modalData,
            header: <>{activity?.isBlackListed ? "Afficher" : "Retirer"} l'activité</>,
            content: <>
                {activity?.isBlackListed ?
                    <Alert variant="info">
                        Cette action rendra cette activité visible par vos utilisateur⋅rices dans leur webapp
                    </Alert> :
                    <Alert variant="info">
                        Cette activité ne sera plus visible par vos utilisateur⋅rices dans leur webapp.
                    </Alert>}
                <Alert variant="warning">
                    Cette modification peut prendre jusqu'à 24h avant d'être visible dans votre webapp.
                </Alert>
                {isParent && <Form.Check
                    name="applyToAllNetwork"
                    id='applyToAllNetwork'
                    type='checkbox'
                    label="Appliquer aussi ce réglage à l'ensemble de mon réseau"
                    defaultChecked={false} />}
            </>,
            cancelButton: 'Annuler',
            onValidate:
                () => {
                    editClientActivityBlacklist(
                        {
                            activityUuid: activity.uniqueId,
                            blacklisted: !activity.isBlackListed,
                            applyToAllNetwork: document.getElementById('applyToAllNetwork')?.checked
                        }
                    ).then(() => {
                        toast.open({ message: "Accès à l'activité modifié", variant: "success" });
                        setModalShow(false)
                    })
                }
        });
        setModalShow(true);
    }

    return (
        <Container>
            <Row className='mb-5' id='header-activity'>
                <Col className='col-auto'>
                    <Row className={"activity-col " + (isMobile ? "my-3" : "")}>
                        <Col className={"col-md-auto " + (isMobile ? "d-flex justify-content-center mb-3" : "")}>
                            {activity?.thumbnail
                                && <Image className="item_details-thumbnail" src={activity.thumbnail} />}
                        </Col>
                        <Col className="d-flex justify-content-between flex-column">
                            <div>
                                <div className='d-flex'>
                                    <h3 className='fw-bold w-auto mr-2'>{activity?.title ?? "Détails de l'activité"}</h3>
                                    {activity?.status && (AuthorizationChecker.hasUpdateRights('activity') || activity?.status == 'lab') && <div className={`status-square mb-2 ${activity?.status}`}>{activityStatus?.find(st => st.value == activity?.status)?.label}</div>}
                                    {
                                        !AuthorizationChecker.hasUpdateRights('activity') &&
                                        <div className='status-square mb-2'>{activity?.isBlackListed ? "Non affichée" : "Affichée"}</div>
                                    }
                                </div>
                                {AuthorizationChecker.hasUpdateRights('activity') ?
                                    <Button variant="warning" onClick={createStatusModal} className='mb-2'>
                                        Modifier le statut
                                    </Button>
                                    : <Button variant={activity?.isBlackListed ? "success" : "danger"} onClick={() => createBlacklistModal(activity)} className='mb-2 w-auto'>
                                        {activity?.isBlackListed ? "Afficher" : "Retirer"}
                                    </Button>}
                            </div>
                            {activity?.duration && <h5 className="mb-0">Durée estimée : <span dangerouslySetInnerHTML={{ __html: showDuration(activity) }} /></h5>}
                        </Col>
                    </Row>
                </Col>
                <Col className={'d-flex flex-column justify-content-end align-top ' + (isTablet ? 'mb-3' : '')}>
                    <Row className='d-flex justify-content-end pe-3 h-50 align-items-start'>
                        {backButtonComponent}
                    </Row>
                    <Row className='d-flex justify-content-end pb-3'>
                        <p className='w-auto m-0 text-right'>
                            {activity.createdAt != null
                                && "Création : " + HelperMetiers360.getdisplayDateType(activity.createdAt, 'day')}
                            {activity.updatedAt != null
                                && " - MAJ : " + HelperMetiers360.getdisplayDateType(activity.updatedAt, 'day')}
                        </p>
                    </Row>
                    <Row className='p-0'>
                        {
                            (AuthorizationChecker.hasUpdateRights('activity') || AuthorizationChecker.hasDeleteRights('activity'))
                            && <div className="item_details_btn_actions d-flex justify-content-end">
                                {
                                    AuthorizationChecker.hasUpdateRights('activity') ?
                                        <Button variant="secondary" as={Link} to={activitySlugs.update.replace(':uniqueId', activity && activity.uniqueId)}><i className="fas fa-edit"></i> &nbsp;Éditer</Button>
                                        : ''
                                }
                                {AuthorizationChecker.hasDeleteRights('activity') ?
                                    <Button variant="danger" onClick={createDeleteModal}><i className="fas fa-trash-alt"></i> &nbsp;Supprimer</Button>
                                    : null}
                            </div>
                        }
                    </Row>
                </Col>
            </Row>
            <div className="two-blocks">
                <div className="activity-description">
                    <span dangerouslySetInnerHTML={{ __html: activity.description }}></span>
                </div>
                <div className="separator-horizontal"></div>
                <div className='activity-details'>
                    <div className='mb-2'>
                        <strong>Publics cibles : </strong>
                        {displayAudienceTypes}
                    </div>

                    {AuthorizationChecker.isAdmin() && <div className='mb-2'>
                        <strong>Liste client{activity.clientsListData?.length > 1 ? "s" : ""} restreinte : </strong>
                            {clientsList}
                        </div>}
                    {AuthorizationChecker.isAdmin() && activity.slug
                        && <div className='mb-2'>
                            <strong>Slug : </strong>
                            {activity.slug}
                        </div>}
                    {activity.publishedAt != null
                        && <div className='mb-2'>
                            <strong>Publiée : </strong> {HelperMetiers360.getdisplayDateType(activity.publishedAt, 'day')}
                        </div>}
                    {(AuthorizationChecker.isAdmin() && activity.activityJSON) &&
                        <div className='mb-2'>
                            <strong>Objet associé : <pre>{JSON.stringify(activity.activityJSON, null, 2)}</pre>
                            </strong>
                        </div>}
                </div>
            </div>
            {!AuthorizationChecker.isAdmin() && <div className="mt-5">
                <h3>Réalisations de l'activité</h3>
                <Webapp2ActivitiesPDFTable slug={activity.slug} clientUniqueId={clientUniqueId} />
            </div>}

            {modalComponent}
        </Container>
    );
}

export const showDuration = (activity, showLabel = false) => {
    let durationShown = "";
    for (let i = 0; i <= 2; i += 1) {
        if (activity.duration > i) {
            durationShown += " &#10711;";
        } else {
            durationShown += " &#10710;";
        }
    }
    let label = "";
    if (showLabel) {
        label = activity.duration == 1 ? "&nbsp; &nbsp; Courte (< 5mn)"
            : activity.duration == 2 ? "&nbsp; &nbsp; Moyenne (5 à 15mn)"
                : activity.duration == 3 ? "&nbsp; &nbsp; Longue (> 15mn)"
                    : "";
    }
    return durationShown + label;
}

export default ActivityDetailsComponent;
